import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowRight } from '@/icons/Icon';
import {
  WarmupWithDeliverabilityGraph,
  WarmupWithoutDeliverabilityGraph,
} from '@/components/organisms/DeliverabilityGraphs';

/**
 * Post-onboarding explainer dialog — "How TrulyInbox Builds Your Sender
 * Reputation." Fully static, ported from trulyinbox-webui.
 */
export type DeliveryRateImprovedModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type DeliveryRateStat = {
  value: ReactNode;
  label: string;
  description: ReactNode;
};

const DELIVERY_RATE_STATS: DeliveryRateStat[] = [
  {
    value: (
      <>
        <span className="text-destructive">~45%</span>
        <ArrowRight size={18} className="text-muted-foreground" />
        <span className="text-success">91%</span>
      </>
    ),
    label: 'Deliverability Rate',
    description: 'Avg improvement',
  },
  {
    value: (
      <>
        <span className="text-destructive">28%</span>
        <ArrowRight size={18} className="text-muted-foreground" />
        <span className="text-success">{'<5%'}</span>
      </>
    ),
    label: 'Spam Reduction',
    description: 'with active warmup',
  },
  {
    value: <span className="text-success">100%</span>,
    label: 'Spam Rescue Rate',
    description: (
      <>
        Landed in Spam
        <ArrowRight size={14} className="text-muted-foreground" />
        Moved to Inbox
      </>
    ),
  },
];

const DELIVERY_RATE_PROCESS_STEPS = [
  {
    id: 1,
    title: 'Warm-up Mail Sent',
    description: 'We send emails from your mailbox to our network of trusted, active inboxes',
  },
  {
    id: 2,
    title: 'Genuine Engagement',
    description: 'Real accounts open, reply to, and star your emails every single day',
  },
  {
    id: 3,
    title: 'Spam Gets Rescued',
    description: 'Any email caught in spam is immediately moved back to the inbox',
  },
  {
    id: 4,
    title: 'ESPs Start Trusting You',
    description: "Google, Microsoft & other ESPs learn your reputation is real, inbox rate climbs",
  },
] as const;

export function DeliveryRateImprovedModal({ open, onOpenChange }: DeliveryRateImprovedModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>How TrulyInbox Builds Your Sender Reputation</DialogTitle>
          <DialogDescription>Average journey across 40,000+ warm-up accounts</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <div className="grid grid-cols-[1fr_auto_1fr] overflow-hidden rounded-lg border">
            <div
              className="flex flex-col items-center gap-1 pl-6 pr-0 pb-4 pt-6"
              style={{ background: 'var(--bad-50)' }}
            >
              <span className="text-lg font-semibold text-destructive">Where most accounts start</span>
              <WarmupWithoutDeliverabilityGraph />
              <span className="mt-auto text-sm text-muted-foreground">Stagnant / declining</span>
            </div>

            <div className="relative flex justify-center">
              <div
                className="h-full border-l-2 border-dashed"
                style={{ borderColor: 'var(--warn-500)' }}
              />
            </div>

            <div
              className="flex flex-col items-center gap-1 pl-0 pr-6 pb-4 pt-6"
              style={{ background: 'var(--good-50)' }}
            >
              <span className="text-lg font-semibold text-success">Where you&rsquo;re headed</span>
              <WarmupWithDeliverabilityGraph />
              <span className="mt-auto text-sm text-muted-foreground">Steady improvement over time</span>
            </div>
          </div>

          {/* Rendered outside the grid's `overflow-hidden` (needed to clip the panel
              backgrounds to the rounded corners) so the pill isn't cropped at its top. */}
          <span
            className="absolute left-1/2 top-0 z-10 inline-flex h-7 min-w-[7.75rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center whitespace-nowrap rounded-full px-3 text-xs font-semibold text-white"
            style={{ background: 'var(--brand-500)' }}
          >
            Warmup Started
          </span>
        </div>

        <div className="grid grid-cols-3 divide-x rounded-lg border">
          {DELIVERY_RATE_STATS.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-1 px-6 py-4 text-center">
              <span className="inline-flex flex-wrap items-center justify-center gap-1.5 text-2xl font-semibold">
                {stat.value}
              </span>
              <span className="mt-1 text-xs font-semibold tracking-wide text-foreground">
                {stat.label}
              </span>
              <span className="inline-flex flex-wrap items-center justify-center gap-1 text-xs text-muted-foreground">
                {stat.description}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <span className="text-xs font-semibold tracking-wide text-foreground">THE WARMUP PROCESS</span>
          <div className="flex items-start gap-3">
            {DELIVERY_RATE_PROCESS_STEPS.map((step, index) => (
              <div key={step.id} className="flex flex-1 items-start gap-3">
                <div className="flex flex-1 flex-col items-center gap-2 text-center">
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white"
                    style={{ background: 'var(--brand-500)' }}
                  >
                    {step.id}
                  </span>
                  <span className="text-sm font-semibold text-foreground">{step.title}</span>
                  <span className="text-xs text-muted-foreground">{step.description}</span>
                </div>
                {index < DELIVERY_RATE_PROCESS_STEPS.length - 1 && (
                  <ArrowRight size={18} className="mt-1.5 shrink-0 text-muted-foreground" aria-hidden />
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
